import NiceModal, { useModal } from "@ebay/nice-modal-react";
import VerdantProvider from "@/components/VerdantProvider.jsx";
import { Sheet } from "react-modal-sheet";
import {
	type ComponentProps,
	ComponentRef,
	memo,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import isEqual from "react-fast-compare";

import { useMemoizedFn, useRequest } from "ahooks";
import { Button, DatePicker, Form, Input, Tab, Tabs } from "@heroui/react";
import { type ExpenseInit, hooks } from "@/store.js";
import { parseDate } from "@internationalized/date";
import dayjs from "dayjs";
import { CATEGORY, PAY_METHOD } from "../../const.js";
import { getDataService } from "@/pages/PlanDetail/service.js";
import { useEventSelect } from "@/components/EventSelect/index.jsx";
import { CURRENCY_CONFIG } from "@/const.js";

interface IProps {
	planId: string;
	defaultValue?: Partial<ExpenseInit>;
}

const Content = memo(
	({
		onClose,
		planId,
		defaultValue: defaultValueOrigin,
	}: { onClose: () => void } & IProps) => {
		const client = hooks.useClient();
		const { id, ...defaultValue } = defaultValueOrigin || {};
		const [planInfo] = useState(() => getDataService({ id: planId }));

		const extraRef = useRef({
			category: "meal",
			payMethod: "cash",
			...defaultValue,
		});
		const [amount, setAmount] = useState(
			defaultValue?.amount ? defaultValue?.amount.toString() : "",
		);

		const [currency, setCurrency] = useState(defaultValue.currency || "CNY");
		const eventSelectModal = useEventSelect();

		const [eventId, setEventId] = useState(defaultValue.eventId);
		const { data: eventData } = useRequest(
			async () => (eventId ? client.events.get(eventId).resolved : undefined),
			{ refreshDeps: [eventId] },
		);

		const eventName = useMemo(() => {
			if (eventData?.get("type") === "airplane") {
				return eventData.get("airplane")?.get("no");
			}

			return eventData?.get("id");
		}, [eventData]);

		return (
			<Form
				className="w-full !block"
				validationBehavior="native"
				onSubmit={async (e) => {
					e.stopPropagation();
					e.preventDefault();
					const data = {
						...extraRef.current,
						...Object.fromEntries(new FormData(e.currentTarget)),
						amount: amount ? Number(amount) : 0,
						currency,
						planId,
						eventId,
					};

					if (id) {
						(await client.expense.get(id).resolved)?.update(data);
					} else {
						// STEP: 创建记录
						await client.expense.put(data);
					}

					onClose();
				}}
				// ref={formRef}
			>
				<header className=" flex items-center text-lg  justify-center relative font-semibold text-gray-800 text-center">
					添加支出
					<Button
						className=" absolute right-4 font-bold"
						size="sm"
						radius="full"
						color="primary"
						type="submit"
					>
						完成
					</Button>
				</header>

				{/* @ts-ignore children */}
				<Tabs
					radius="full"
					size="lg"
					variant="underlined"
					defaultSelectedKey={extraRef.current.category}
					classNames={{
						//      tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
						cursor: " bg-blue-600",
						//   tab: "max-w-fit px-0 h-12",
						//   tabContent: "group-data-[selected=true]:text-[#06b6d4]",
					}}
					style={{ "--heroui-foreground": "218 82% 53%" }}
					onSelectionChange={(e: string) => {
						extraRef.current.category = e;
					}}
					className="flex mx-5 mt-5"
				>
					{CATEGORY.map((i) => (
						<Tab
							key={i.value}
							title={
								<span className="flex items-center">
									<i className={`ic-twotone ${i.icon} mr-1 `} />
									{i.label}
								</span>
							}
						/>
					))}
				</Tabs>

				<div className="flex items-center justify-center text-5xl leading-none font-bold mt-6 ">
					<span className="text-blue-600 text-4xl leading-none font-[din] -ml-6 w-8 mr-2">
						{CURRENCY_CONFIG[currency]?.unit}
					</span>
					<div className=" relative font-[din]">
						{/* <input
              placeholder='0.00'
              className=' absolute w-full outline-none font-[din]'
              type='number'
              inputMode='decimal'
              ref={inputRef}
            /> */}
						{`${Number(amount).toLocaleString()}${
							amount.slice(-1) === "." ? "." : ""
						}` || "0.00"}
					</div>
				</div>

				<Input
					variant="underlined"
					className="mb-4 mt-4 mx-auto w-max  "
					classNames={{
						input: "text-center opacity-70",
						inputWrapper: "border-b-1 shadow-none min-h-7  h-7",
					}}
					placeholder="备注"
					name="title"
					defaultValue={defaultValue?.title}
				/>

				<div className=" flex justify-center items-center mb-4">
					<Button
						size="sm"
						variant="ghost"
						radius="full"
						className="border-1 text-gray-600"
						startContent={<i className="ic-round md-link rotate-45 -mr-0.5" />}
						onPress={() => {
							eventSelectModal.show({
								planId,
								onSubmit(payload) {
									setEventId(payload?.[0]);
								},
								defaultValue: eventId ? [eventId] : undefined,
							});
						}}
					>
						{eventName || "关联活动"}
					</Button>
				</div>

				<div className="flex items-center justify-between px-4">
					{/* @ts-ignore children */}
					<Tabs
						size={"sm"}
						radius="full"
						defaultSelectedKey={extraRef.current.payMethod}
						onSelectionChange={(e: string) => {
							extraRef.current.payMethod = e;
						}}
						// className='overflow-auto px-6'
					>
						{PAY_METHOD.map((i) => (
							<Tab key={i.value} title={i.label} />
						))}
					</Tabs>
					<DatePicker
						className="max-w-[140px]"
						aria-label="date"
						dateInputClassNames={{ inputWrapper: "px-4" }}
						size="sm"
						radius="full"
						// @ts-ignore type
						defaultValue={parseDate(
							defaultValue?.date || dayjs().format("YYYY-MM-DD"),
						)}
						name="date"
					/>
				</div>
				<div className="grid grid-cols-2 mt-5">
					{[
						{ label: "￥人民币", value: "CNY" },
						...(planInfo?.currency?.map((i) => ({
							label: `${CURRENCY_CONFIG[i].unit}${CURRENCY_CONFIG[i].name}`,
							value: i,
						})) || []),
					].map((i) => {
						const isActive = currency === i.value;
						return (
							<article
								key={i.value}
								onClick={() => {
									setCurrency(i.value);
								}}
								className={`px-6 text-center py-2 border-t-2 ${
									isActive
										? "text-gray-800 font-semibold bg-white border-blue-600"
										: "border-transparent bg-gradient-to-b to-white  from-gray-50 text-gray-500"
								}`}
							>
								{i.label}
							</article>
						);
					})}
				</div>
				<div className="grid grid-cols-3 mt-6">
					{[1, 2, 3, 4, 5, 6, 7, 8, 9, ".", 0, "CLEAR"].map((i) => {
						return (
							<Button
								variant="light"
								size="lg"
								className="text-2xl h-16 font-semibold font-[din]"
								key={i}
								onPress={() => {
									setAmount((prev) => {
										if (i === "CLEAR") {
											return prev.slice(0, -1);
										}
										const [integer, decimal] = prev.split(".");
										if (decimal?.length === 2) return prev;
										return prev + i;
									});
								}}
							>
								{i === "CLEAR" ? (
									<i className="ic-round md-remove_circle_outline" />
								) : (
									i
								)}
							</Button>
						);
					})}
				</div>
			</Form>
		);
	},
	isEqual,
);
const Add = NiceModal.create((props: IProps) => {
	const modal = useModal();
	const handleClose = useMemoizedFn(() => {
		modal.hide();
		setTimeout(() => {
			modal.remove();
		}, 300);
	});

	return (
		<VerdantProvider>
			<Sheet
				isOpen={modal.visible}
				onClose={handleClose}
				detent="content-height"
				disableScrollLocking
				style={{ zIndex: 50 }}
			>
				<Sheet.Container className="!rounded-t-3xl  ">
					<Sheet.Header />
					<Sheet.Content className="pb-safe">
						<Content {...props} onClose={handleClose} />
					</Sheet.Content>
				</Sheet.Container>
				<Sheet.Backdrop
					className="!bg-black/55"
					onTap={(e) => {
						modal.hide();
						setTimeout(() => {
							modal.remove();
						}, 500);
					}}
				/>
			</Sheet>
		</VerdantProvider>
	);
});

export const useAdd = (props: { planId?: string }) => {
	const modal = useModal(Add);
	const show = useMemoizedFn((payload: Omit<IProps, "planId">) => {
		if (!props.planId) return;
		return modal.show({ ...props, ...payload });
	});
	return {
		show,
	};
};
export default Add;
